/**
 * 部門API クライアント
 */

import { authFetch, API_BASE_URL } from "./client";

/**
 * 部門レスポンスの型
 */
export interface DepartmentResponse {
    id: number;
    /** 店舗別の部門組織 (level=12) を指す。テナント単位の部門マスタ取得では含まれない */
    organization_id?: number;
    code: string;
    name: string;
}

/**
 * 店舗部門一覧レスポンスの型
 */
export interface DepartmentListResponse {
    items: DepartmentResponse[];
    total: number;
}

/**
 * 指定店舗の部門一覧を取得
 */
export async function fetchStoreDepartments(
    storeId: number
): Promise<DepartmentListResponse> {
    const url = `${API_BASE_URL}/stores/${storeId}/departments`;

    const response = await authFetch(url);
    if (!response.ok) {
        throw new Error(`部門一覧の取得に失敗しました: ${response.status}`);
    }
    return response.json();
}

/**
 * テナント単位の部門マスタ一覧を取得
 */
export async function fetchDepartments(): Promise<DepartmentListResponse> {
    const response = await authFetch(`${API_BASE_URL}/departments`);
    if (!response.ok) {
        throw new Error(`部門一覧の取得に失敗しました: ${response.status}`);
    }
    return response.json();
}

/**
 * 部門作成リクエストの型
 */
export interface DepartmentCreateRequest {
    code: string;
    name: string;
}

/**
 * 部門更新リクエストの型
 */
export interface DepartmentUpdateRequest {
    code?: string;
    name?: string;
}

/**
 * 部門を作成
 */
export async function createDepartment(
    data: DepartmentCreateRequest,
): Promise<DepartmentResponse> {
    const response = await authFetch(`${API_BASE_URL}/departments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
    });
    if (!response.ok) {
        const body = await response.text();
        throw new Error(`部門の作成に失敗しました: ${response.status} ${body}`);
    }
    return response.json();
}

/**
 * 部門を更新
 */
export async function updateDepartment(
    departmentId: number,
    data: DepartmentUpdateRequest,
): Promise<DepartmentResponse> {
    const response = await authFetch(`${API_BASE_URL}/departments/${departmentId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
    });
    if (!response.ok) {
        const body = await response.text();
        throw new Error(`部門の更新に失敗しました: ${response.status} ${body}`);
    }
    return response.json();
}

/**
 * 部門を削除（論理削除）
 */
export async function deleteDepartment(departmentId: number): Promise<void> {
    const response = await authFetch(`${API_BASE_URL}/departments/${departmentId}`, {
        method: "DELETE",
    });
    if (!response.ok) {
        throw new Error(`部門の削除に失敗しました: ${response.status}`);
    }
}
